<script setup lang="ts">
  defineOptions({ name: 'CPCheckbox', inheritAttrs: false })

  // External library imports
  import type { PropType } from 'vue'

  import { HLCheckbox } from '@platform-ui/highrise'

  // Type imports

  // Props definitions
  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    checked: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    indeterminate: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      required: false,
      default: 'md',
    },
    label: {
      type: String,
      required: false,
      default: undefined,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    'update:checked': [checked: boolean, event: Event]
  }>()

  // Event handlers
  const handleUpdateChecked = (checked: boolean, event: Event): void => {
    emit('update:checked', checked, event)
  }
</script>

<template>
  <HLCheckbox
    v-bind="$attrs"
    :id="props.id"
    class="cp-checkbox"
    :checked="props.checked"
    :disabled="props.disabled"
    :indeterminate="props.indeterminate"
    :size="props.size"
    :label="props.label"
    @update:checked="handleUpdateChecked"
  >
    <slot />
  </HLCheckbox>
</template>

<!--
  HighRise paints checkbox copy as .hr-text { color: var(--gray-600) } and
  sets --n-text-color to the HighRise gray-900 token. Those gray tokens
  stay navy in dark mode. Remap them on .cp-checkbox so HighRise's own
  color: var(--gray-600) resolves to --cp-brand-font. Unscoped + !important
  beats inline --n-* vars.
-->
<style>
  .cp-checkbox {
    --n-text-color: var(--cp-brand-font) !important;
    --n-text-color-disabled: var(--cp-font-400) !important;
    --gray-600: var(--cp-brand-font);
    --gray-900: var(--cp-brand-font);
  }

  .cp-checkbox,
  .cp-checkbox .hr-text,
  .cp-checkbox .hr-checkbox__label,
  .cp-checkbox .n-checkbox__label {
    color: var(--cp-brand-font) !important;
    -webkit-text-fill-color: var(--cp-brand-font) !important;
  }
</style>
